import React, { useState, useEffect, useCallback } from 'react';
import { ListSubheader, Button, Icon, Typography, Grid, TextField, LinearProgress } from '@mui/material';
import ScoreIcon from 'global/components/ScoreIcon';
import CheckpointForm from 'global/components/form-checkpoint';
import useStyles from './ChecklistScores.styles';
import clsx from 'clsx';
import { get, size, set, sortBy, unset, flatten, last } from 'global/lib/lodash';
import { getId } from '../AssessmentScore';
import { useScreen } from 'global/styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useIntersect } from 'global/hooks/useIntersect';
import ModalGateway from 'global/components/ModalGateway';
import { getImageSource, uppercaseFirst, guid } from 'global/utils';
import Link from 'react-router-dom/Link';
import { getTaskStatus } from 'global/utils';
import useDebounce from 'global/hooks/useDebounce';
import { setTaskList } from 'redux/actions/view';
import { useDispatch } from 'react-redux';
import { DetailModal } from '../../task/TaskDetail';
import { CreateModal } from '../../task/CreateModal';
import EImage, { ImageList } from 'global/components/image';
import SnackAlerts from 'global/components/snack-alerts';
import { Loading } from 'global/styles';
import LibraryIcon from '@mui/icons-material/Book';
import TasksComments from './tasksComments';
import { useQueue } from 'global/hooks/useQueue';
import { MoreDrop } from 'global/components/more-drop';
import { useUser } from 'global/hooks/useUser';
import { Column, Row } from 'global/components/row';
import { Button as Btn } from 'global/components/Button';
import colors from 'global/styles/colors';
import * as moment from 'moment';
import { Text } from 'global/components/text';
import sendData from 'redux/actions/sendData';
import { _size, cleanCopy, shouldUseSubEditView } from '../../../global/utils';
import { ObservationCreateModal, SubscoreForm, deleteSubScore, modifySubScore, ObservationCreateFab, getDisplayConfigFrom, ObservationGrid, ObservationCollapse } from '../AssessmentScore/ObservationCreate';
import { Button as OButton } from '../../../global/components/Button';
import { AUTOTAG } from '../AssessmentScore/ObservationCreate/ScoreTag';

export default (props) => {
  const {
    checklist,
    assessment,
    setActiveEdit,
    activeEdit,
    updateScores,
    setCurrentAnchor,
    selectItem,
    isSelected,
    assessmentTasks,
    hasSelectedItems,
    showSelectBoxes,
    userCanEdit,
    canShowScores,
    scorecard,
    userCanLink,
    searchString,
    system,
    updateAssessmentTasks,
    isCourse,
    isTemplate,
  } = props;

  const [modal, setModal] = useState(false);
  const hasImage = get(checklist, 'checklist.image');
  const image = hasImage ? getImageSource(hasImage.large) : null;

  const [stickyRef, stickyEntry] = useIntersect({ rootMargin: '-40px 0px -80% 0px' });

  const [alerts, setAlerts] = useState();
  const { queue, clearQueue, addToQueue } = useQueue({ onUpdate: updateScores, setAlerts, system });

  const calculatedScore = get(checklist, 'value');

  const loading = !!size(queue);
  const classes = useStyles();

  useEffect(() => {
    const y = get(stickyEntry, 'isIntersecting', false);
    if (y) {
      setCurrentAnchor && setCurrentAnchor(get(props, 'checklist'));
    }
  }, [stickyEntry]);

  useEffect(() => {
    clearQueue();
  }, [activeEdit]);

  const onEditPress = useCallback((e) => {
    if (activeEdit) {
      // User is closing the edit mode
      setActiveEdit();
      // Site redirects user/sidebar to the Checklist they were just editing
      setCurrentAnchor(checklist, true);
    } else {
      // User is entering edit mode, screen scrolls to edit section
      setActiveEdit({ ...props, scroll: true, scrollBy: -16 });
      setCurrentAnchor(checklist);
    }
  }, [checklist]);

  const onImageClick = useCallback((img) => {
    setModal({ index: 0 });
  }, []);

  const id = getId(checklist);
  const currentSelect = isSelected(checklist);

  const ScoreIconProps = {
    ...(!activeEdit
      ? {
          useBlankCheckbox: showSelectBoxes,
          selected: showSelectBoxes && get(currentSelect, 'fullCount'),
          indeterminate: showSelectBoxes && currentSelect,
          onClick: () => selectItem(checklist),
        }
      : {}),

    score: calculatedScore,
  };

  const hasGenObs = !get(checklist, 'children.0.checkpointId');
  const pChildren = activeEdit && !hasGenObs ? [{}, ...get(checklist, 'children', [])] : get(checklist, 'children', []);
  const isCategory = get(props, 'filter.listGroup') === 'category';
  const isLocation = get(props, 'filter.listGroup') === 'location';
  const onlyLoc = get(props, 'filter.onlyLoc', false);
  const showChildren = isCourse || activeEdit || searchString || get(checklist, 'displayScore') !== 'N/A' || hasGenObs;

  const renderItem = (checkpoint, i) => {
    const isObs = !checkpoint.checkpointId;
    const k = `${checklist._id}_${isObs ? 'obs' : get(checkpoint, 'checkpointId', i)}`;
    const hasChildren = isCategory && !!size(get(checkpoint, 'subscores'));
    const showCheckpoint = onlyLoc ? checkpoint.isSubScore || hasChildren : true;

    const items = (() => {
      const subScores = [];
      const autoSubs = [];
      let autoRender;

      if (!hasChildren) { return { subScores, autoSubs } }
      
      get(checkpoint, 'subscores', []).map(ss => {
        if (ss.notes == AUTOTAG) {
          autoRender = {
            ...(autoRender || {}),
            ...checkpoint, 
            ...ss, 
            assessmentId: '', 
            subscores: null, 
            isSubScore: true, 
            notes: '',
            criteria: get(autoRender, 'criteria') ? `${get(autoRender, 'criteria')}; ${get(ss, 'realm.name')}` : get(ss,'realm.name'), 
            name: `✔`, 
            isAgg: true 
          }
          autoSubs.push(ss)
        } else {
          subScores.push({ ...checkpoint, ...ss, assessmentId: '', subscores: null, isSubScore: true })
        }
      })

      if (size(subScores) === 0 && size(autoSubs) === 1) {
        return { subScores, autoSubs: [], autoRender: undefined }
      }

      return { subScores, autoSubs, autoRender }
    })();

    if (isObs && i !== 0) {
      return null;
    }

    return activeEdit ? (
      <EditScore {...props} key={k} assessment={assessment} scorecard={scorecard} queue={queue} addToQueue={addToQueue} checklist={checklist} checkpoint={checkpoint} />
    ) : (
      <>
        {showCheckpoint && (
          <CheckpointScore
            {...props}
            key={k}
            searchString={searchString}
            updateAssessmentTasks={updateAssessmentTasks}
            scorecard={scorecard}
            canShowScores={canShowScores}
            assessmentTasks={assessmentTasks}
            hasSelectedItems={hasSelectedItems}
            showSelectBoxes={showSelectBoxes}
            isSelected={isSelected}
            selectItem={selectItem}
            checkpoint={{ ...checkpoint, isSubScore: checkpoint.isSubScore && !hasChildren }}
            checklist={checklist}
            assessment={assessment}
          />
        )}
        {hasChildren && (
          <div style={{ marginLeft: 16 }}>
            {!!items.autoRender && renderItem(items.autoRender, 0)}
            {items.subScores.map((ss, i) => renderItem(ss, i + size(items.subScores)))}
          </div>
        )}
      </>
    );
  };

  const renderItems = () => {
    const subEditView = isTemplate || isCourse ? false : shouldUseSubEditView(get(scorecard, 'config'));
    let lastSub;
    if (subEditView) {
      const f = pChildren.find((c) => c.isSubScore || !!size(c.subscores));
      lastSub = get(f, 'isSubScore') ? f : get(f, 'subscores.0', f);
      if (lastSub) {
        lastSub = cleanCopy(lastSub);
        set(lastSub, '_subscore.images', []);
        set(lastSub, '_subscore.notes', '');
        set(lastSub, '_subscore.isadd', true);

        unset(lastSub, '_subscore.id');
        if (isCategory) {
          unset(lastSub, '_subscore.realm');
        } else {
          set(lastSub, '_subscore.tag', null);
        }
      } else if (isCategory) {
        lastSub = {
          ...last(get(props, 'checklist.children', [{}])),
          supersetId: get(props, 'checklist.supersetId'),
          checklist: get(props, 'checklist.checklist'),
          _subscore: {},
        };
      }
    }

    const children = subEditView && activeEdit ? (isLocation ? pChildren.filter((p) => p.checkpointId) : pChildren) : pChildren;

    return (
      <>
        {subEditView && activeEdit && isLocation && !isTemplate && (
          <Row center>
            <ObservationCreateFab {...props} style={{ marginLeft: 16, marginBottom: 8 }} openTagToSelected={true} activeEdit={activeEdit} hasSubScores={true} />
          </Row>
        )}
        {children.map(renderItem)}
        {subEditView && activeEdit && isLocation && !isTemplate && (
          <Row center>
            <ObservationCreateFab {...props} style={{ marginLeft: 16, marginBottom: 8 }} openTagToSelected={true} activeEdit={activeEdit} hasSubScores={true} />
          </Row>
        )}
      </>
    );
  };

  return (
    <div key={id} id={id}>
      <ListSubheader ref={stickyRef} className={clsx(isCourse ? classes.checklistHeaderCourse : classes.checklistHeader, showChildren && classes.checklistHeaderBorder)} disableGutters={true}>
        {!isCourse && <h3 className={classes.cpScoreHeaderChild}>{checklist.displayScore}</h3>}
        {!isCourse && <ScoreIcon {...ScoreIconProps} />}
        <div className={classes.headerChildName}>
          <h3 title={checklist.name} className={classes.headerChildName}>
            {checklist.name}
          </h3>
          {get(checklist, 'criteria') && <span className={classes.headerCriteria}>{get(checklist, 'criteria')}</span>}
        </div>
        <div className={classes.grow} />
        {!activeEdit && !isCourse && userCanLink && (
          <Button component={Link} classes={{ root: classes.buttonRoot }} to={`/library/${scorecard.editionId}/document/${get(checklist, 'checklist.classificationId', '')}`}>
            <LibraryIcon fontSize="small" color="secondary" />
          </Button>
        )}
        {activeEdit && <PrevNext {...props} loading={loading} stickyEntry={stickyEntry} />}
        {(userCanEdit || activeEdit) && !props.isReview && (
          <Button className={activeEdit ? classes.headerBtn : null} color={activeEdit ? 'primary' : 'secondary'} onClick={() => onEditPress()}>
            <Icon fontSize="small">{activeEdit ? (loading ? Loading : 'close') : 'edit'}</Icon>
            <span className={classes.edit}>{activeEdit ? 'View' : 'Edit'}</span>
          </Button>
        )}
      </ListSubheader>
      {showChildren && (
        <>
          <Grid container spacing={0} justifyContent="space-around" direction={'row-reverse'}>
            <Grid item xs={12} className={classes.spacerTop} />
            {hasImage && (
              <Grid item xs={9} md={5} className={classes.checklistImage}>
                <EImage onClick={onImageClick} useNaturalDims={false} src={image} aspectRatio={0.75} unsetWidth unsetHeight />
              </Grid>
            )}
            <Grid item xs={12} md={hasImage ? 7 : 12} style={{ position: 'relative' }}>
              {renderItems()}
              {activeEdit && <PrevNext {...props} loading={loading} stickyEntry={stickyEntry} type={"fixed-bottom"} />}
            </Grid>
            <Grid item xs={12} className={classes.spacerBottom} />
          </Grid>

          <ModalGateway
            open={modal}
            onClose={() => setModal(false)}
            currentIndex={get(modal, 'index')}
            images={[hasImage]
              .filter((f) => f)
              .map((x, i) => ({
                key: i,
                src: getImageSource(get(x, 'large')),
                caption: get(x, 'caption'),
              }))}
          />
        </>
      )}
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
};

export const ChecklistScoresPrint = (props) => {
  const { checklist, assessment, assessmentTasks, canShowScores, scorecard, searchString, notFirst } = props; //updateAssessmentTasks, subscores, filter
  const hasImage = get(checklist, 'checklist.image');
  const image = hasImage ? getImageSource(hasImage.large) : null;
  const calculatedScore = get(checklist, 'value');

  const classes = useStyles();

  const id = getId(checklist);

  const ScoreIconProps = {
    score: calculatedScore,
  };

  const hasGenObs = !get(checklist, 'children.0.checkpointId');
  const pChildren = get(checklist, 'children', []);
  const isCategory = get(props, 'filter.listGroup') === 'category';
  // const isLocation = get(props, 'filter.listGroup') === 'location';
  const onlyLoc = get(props, 'filter.onlyLoc', false);
  const showChildren = searchString || get(checklist, 'displayScore') !== 'N/A' || hasGenObs;

  const renderItem = (checkpoint, i) => {
    const k = `${checklist._id}_${get(checkpoint, 'checkpointId', i)}`;
    const hasChildren = isCategory && !!size(get(checkpoint, 'subscores'));
    const showCheckpoint = onlyLoc ? checkpoint.isSubScore || hasChildren : true;
    const isObs = !checkpoint.checkpointId;

    if (isObs && i !== 0) {
      return null;
    }

    return (
      <>
        {showCheckpoint && (
          <CheckpointScorePrint
            {...props}
            key={k}
            searchString={searchString}
            scorecard={scorecard}
            canShowScores={canShowScores}
            assessmentTasks={assessmentTasks}
            checkpoint={{ ...checkpoint, isSubScore: checkpoint.isSubScore && !hasChildren }}
            checklist={checklist}
            assessment={assessment}
            showNotes={!hasChildren}
          />
        )}
        {hasChildren && (
          <div style={{ marginLeft: 16 }}>{get(checkpoint, 'subscores', []).filter(s => s.notes !== AUTOTAG).map((ss, i) => renderItem({ ...checkpoint, ...ss, assessmentId: '', subscores: null, isSubScore: true }, i))}</div>
        )}
      </>
    );
  };

  return (
    <div key={id} id={id} className={notFirst ? classes.checklist : classes.checklistFirst}>
      <ListSubheader disableSticky={true} className={clsx(classes.checklistHeader, showChildren && classes.checklistHeaderBorder)} disableGutters={true}>
        <h3 className={classes.cpScoreHeaderChild}>{checklist.displayScore}</h3>
        <ScoreIcon {...ScoreIconProps} />
        <div className={classes.headerChildName}>
          <h3 title={checklist.name} className={classes.headerChildName}>
            {checklist.name}
          </h3>
          {get(checklist, 'criteria') && <span className={classes.headerCriteria}>{get(checklist, 'criteria')}</span>}
        </div>
        <div className={classes.grow} />
      </ListSubheader>
      {showChildren && (
        <Grid container spacing={0} justifyContent="space-around" direction={'row-reverse'}>
          <Grid item xs={12} className={classes.spacerTop} />
          {hasImage && (
            <Grid item xs={9} md={5} className={classes.checklistImage}>
              <EImage useNaturalDims={false} src={image} aspectRatio={0.75} unsetWidth unsetHeight />
            </Grid>
          )}
          <Grid item xs={12} md={hasImage ? 7 : 12}>
            {pChildren.map(renderItem)}
          </Grid>
          <Grid item xs={12} className={classes.spacerBottom} />
        </Grid>
      )}
    </div>
  );
};

const PrevNext = (props) => {
  const { setActiveEdit, setCurrentAnchor, loading, stickyEntry } = props;
  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });

  const prev = get(props, 'prev');
  const next = get(props, 'next');

  const hasPrev = !!get(prev, 'id');
  const hasNext = !!get(next, 'id');

  const prevPress = useCallback((e) => {
    if (loading) {
      return;
    }
    setActiveEdit({
      ...props,
      system: get(prev, '_system', get(props, 'system')),
      checklist: prev,
      // We only scroll to top on prev-next if the sticky is near the top of the page,
      // because it's jarring for the user to be scrolled down when the sticky isn't sticky
      scroll: get(stickyEntry, 'isIntersecting', false),
      scrollBy: 48,
    });
    setCurrentAnchor(prev, true);
  }, [stickyEntry]);

  const nextPress = useCallback((e) => {
    if (loading) {
      return;
    }
    setActiveEdit({
      ...props,
      system: get(next, '_system', get(props, 'system')),
      checklist: next,
      scroll: get(stickyEntry, 'isIntersecting', false),
      scrollBy: 48,
    });
    setCurrentAnchor(next, true);
  }, [stickyEntry]);

  if (props.type === 'fixed-bottom') {
    return (
      <div className={classes.fixedBottomNav}>
        {prev && (
          <OButton icon="keyboard_arrow_up" loading={loading} onClick={prevPress} disabled={!hasPrev}>
            PREV
          </OButton>
        )}
        {next && (
          <OButton icon="keyboard_arrow_down" loading={loading} disabled={!hasNext} onClick={nextPress}>
            NEXT
          </OButton>
        )}
      </div>
    );
  }

  return (
    <>
      {prev && (
        <Button color={'secondary'} onClick={prevPress} disabled={!hasPrev} size={isMobile ? 'small' : undefined} classes={{ root: classes.buttonRoot }}>
          <Icon>{loading ? Loading : 'keyboard_arrow_up'}</Icon>
          <span className={classes.edit}>PREV</span>
        </Button>
      )}
      {next && (
        <Button color={'secondary'} disabled={!hasNext} onClick={nextPress} size={isMobile ? 'small' : undefined} classes={{ root: classes.buttonRoot }}>
          <Icon>{loading ? Loading : 'keyboard_arrow_down'}</Icon>
          <span className={classes.edit}>NEXT</span>
        </Button>
      )}
    </>
  );
};

export const CommentContainer = (props) => {
  const { me } = useUser();
  const classes = useStyles();

  const { checkpoint, isCheckpointEditMode } = props;

  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState();
  const [comment, setComment] = useState({
    author: get(props, 'comment.authorName') || get(props, 'comment.authorEmail', ''),
    body: '',
    createdAt: undefined,
    images: null,
    meta: {},
    ...(props.comment || {}),
  });

  const onChange = (e) => {
    setComment({ ...comment, body: e.target.value });
  };

  const save = async () => {
    try {
      setLoading(true);
      const { scorecardScoreId } = checkpoint;
      await sendData({
        url: '/comments',
        method: 'post',
        data: {
          data: {
            type: 'comments',
            attributes: {
              user_id: me.id,
              ...comment,
              commentable_id: `${scorecardScoreId}`,
              commentable_type: 'scorecard_score',
            },
          },
        },
      });
      props.onSave && (await props.onSave(comment));
      props.onClose();
    } catch (err) {
      setAlerts({ message: `Error saving: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={clsx(classes.rComment, isCheckpointEditMode && classes.rCommentCheckpointEdit)}>
      <Column fullWidth className="column">
        <Row fullWidth className="textRow">
          <Text type="label_medium">{moment(comment.createdAt).format('L h:mma')}</Text>
          <Text type="label_medium">{comment.author}</Text>
          <div className="indicator" />
        </Row>
        <div className="textContainer">
          {props.comment && <Text type="label_medium">{comment.body}</Text>}
          {!props.comment && <TextField multiline size="small" rows={4} variant="outlined" value={comment.body} onChange={onChange} />}
        </div>
        {!props.comment && (
          <Row fullWidth className="btnRow">
            <Btn bgColor={colors.black} onClick={props.onClose}>
              CANCEL
            </Btn>
            <Btn loading={loading} onClick={save}>
              SAVE
            </Btn>
          </Row>
        )}
      </Column>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
};

export const CheckpointScore = (props) => {
  const { key, checkpoint, checklist, updateAssessmentTasks, assessment, selectItem, isSelected, assessmentTasks, showSelectBoxes, updateScores } = props;
  const [tasks, setTasks] = useState();
  const [taskModal, setTaskModal] = useState({ open: false });
  const [taskCreate, setTaskCreate] = useState({ open: false });
  const [showComment, setShowComment] = useState(false);
  const [subModal, setSubModal] = useState({ open: false, onClose: () => setSubModal((sm) => ({ ...sm, open: false })) });

  const isSubScore = checkpoint.isSubScore;
  const isAggregateSubScore = isSubScore && get(checkpoint, 'isAgg');
  const hasCriteria = get(checkpoint, 'criteria');
  const showNotes = get(props, 'showNotes', true);
  const hasNotes = get(checkpoint, 'notes');
  const hasImages = get(checkpoint, 'images');
  const cpOpen = showNotes ? (hasNotes || hasImages ? true : false) : false;
  const classes = useStyles(props);
  const dispatch = useDispatch();
  const { me } = useUser();

  const isGobs = get(checkpoint, 'depth') === 2;

  const onOpenTasks = () => {
    dispatch(setTaskList(tasks));
    setTaskModal({
      open: true,
      onClose: () => setTaskModal({ open: false }),
      taskId: get(tasks, '0.id'),
    });
  };

  useEffect(() => {
    if (!assessmentTasks) {
      return;
    }
    const hasTasks = assessmentTasks.filter((at) => {
      const scardid = get(at, 'scorecardScoreId') === get(checkpoint, 'scorecardScoreId');
      if (at.checkpointId === null && isGobs) {
        return scardid;
      }
      if (isSubScore && get(at, 'subScore.id') == get(checkpoint, 'id')) {
        return true;
      }
      return scardid && at.checkpointId == get(checkpoint, 'checkpointId');
    });
    if (hasTasks) {
      setTasks(hasTasks);
    }
  }, [assessmentTasks]);

  const renderTasksCount = () => {
    const counts = {
      open: 0,
      pending: 0,
      closed: 0,
    };
    tasks.map((t) => {
      const { title } = getTaskStatus(t);
      set(counts, title, get(counts, title, 0) + 1);
    });
    const order = ['open', 'pending', 'closed'];
    return order
      .filter((o) => !!get(counts, o))
      .map((o) => `${get(counts, o)} ${uppercaseFirst(o)} Task${get(counts, o) > 1 ? 's' : ''}`)
      .join(', ');
  };

  const task = get(tasks, '0');

  const score = (() => {
    if (isGobs) {
      return 'N/A';
    } else {
      return checkpoint.value;
    }
  })();

  const isCategory = get(props, 'filter.listGroup') === 'category';
  const isSubCat = isSubScore && isCategory;
  const isStaticScore = isCategory && !!size(checkpoint.subscores);

  const name = (() => {
    if (isGobs) {
      return 'General Observations';
    } else {
      if (isSubScore && !isAggregateSubScore) {
        return isSubCat ? get(checkpoint, 'realm.name', checkpoint.name) : `${get(checkpoint, 'checklist.name')}: ${get(checkpoint, 'name')}`;
      }
      return checkpoint.name;
    }
  })();

  const options = [
    { title: 'Select', hide: isAggregateSubScore, onClick: () => selectItem(checkpoint) },
    {
      title: 'Pass',
      hide: !isSubScore || isAggregateSubScore || score === 100 || !props.userCanModify,
      onClick: async () => {
        const result = await modifySubScore({ ...get(checkpoint, '_subscore', {}), score: 100 });
        await updateScores(result);
      },
    },
    {
      title: 'Fail',
      hide: !isSubScore || isAggregateSubScore || score === 0 || !props.userCanModify,
      onClick: async () => {
        const result = await modifySubScore({ ...get(checkpoint, '_subscore', {}), score: 0 });
        await updateScores(result);
      },
    },
    {
      title: 'Delete Instance',
      hide: !isSubScore || isAggregateSubScore || !props.userCanModify,
      onClick: async () => {
        const result = await deleteSubScore({ ...get(checkpoint, '_subscore', {}), score: 0 });
        await updateScores(result);
      },
    },
    {
      title: 'Edit Instance',
      hide: !isSubScore || isAggregateSubScore || !props.userCanModify,
      onClick: async () => {
        setSubModal((sm) => ({ ...sm, open: true, checkpoint }));
      },
    },
    {
      title: 'Create Task',
      hide: isStaticScore || isAggregateSubScore,
      onClick: () => {
        setTaskCreate({
          open: true,
          checkpoint: checkpoint,
          checklist: get(checkpoint, 'checklist', checklist),
          realmId: get(assessment, 'data.realmId'),
          assignedTo: get(assessment, 'data.assignees', [me]),
          assessment: assessment.data,
          onClose: async (created) => {
            if (created) {
              await updateAssessmentTasks();
            }
            setTaskCreate({ open: false });
          },
        });
      },
    },
    {
      title: 'Respond to Author',
      hide: get(assessment, 'data.status') !== 'review',
      onClick: () => {
        setTaskCreate({
          open: true,
          title: 'Respond to Author',
          checkpoint: checkpoint,
          checklist: checklist,
          realmId: get(assessment, 'data.realmId'),
          assessment: assessment.data,
          assignedTo: get(assessment, 'data.authors', []),
          clientId: get(),
          onClose: async (created) => {
            if (created) {
              await updateAssessmentTasks();
            }
            setTaskCreate({ open: false });
          },
        });
      },
    },
  ].filter((f) => !f.hide);

  const comments = get(
    get(props, 'assessment.data.scorecardScores', []).find((c) => c.id == get(checkpoint, 'scorecardScoreId')),
    'comments',
    []
  );

  const showContent = get(props, 'showContent', true);
  const showDisplayScore = get(props, 'showDisplayScore', true);
  const showDrop = get(props, 'showDrop', true) && !props.isReview && !props.isCourse;

  return isGobs && !hasCriteria ? null : (
    <div className={classes.topic} key={key}>
      <div className={classes.rowTop}>
        <div className={classes.row}>
          {showDisplayScore && (
            <Typography variant="body2" className={classes.cpScoreHeaderChild}>
              {isSubScore || isGobs ? '\xa0' : checkpoint.displayScore}
            </Typography>
          )}
          <ScoreIcon
            useBlankCheckbox={isSubScore && isCategory ? false : showSelectBoxes}
            selected={showSelectBoxes && isSelected(checkpoint)}
            onClick={() => selectItem && selectItem(checkpoint)}
            center
            score={score}
            isBw={false}
            disabled={isSubScore && isCategory}
          />
        </div>
        <Typography variant="body2" style={{marginTop:'2px'}}>
          <strong>{name}</strong>
          &nbsp;
          {hasCriteria}
        </Typography>
        <div className={classes.moreIcon}>
          {props.isReview && get(assessment, 'data.status') !== 'completed' && (
            <Button onClick={() => setShowComment(!showComment)}>
              <Icon>add_comment</Icon>
            </Button>
          )}
          {showDrop && get(checkpoint, 'scorecardScoreId') && <MoreDrop options={options} />}
        </div>
      </div>
      {showContent && (
        <>
          {cpOpen && (
            <div className={classes.cpscoreHidden}>
              {!!hasNotes && <div className={classes.criteria}>{`Notes: ${hasNotes || ''}`}</div>}
              {!!hasImages && (
                <div className={classes.images}>
                  <ImageList style={classes.image} imageSize={100} spaceBetween={3} showCaption srcOverride={{ full: 'large', thumb: 'thumb' }} images={sortBy(hasImages, 'orderColumn')} />
                </div>
              )}
            </div>
          )}
          {task && (
            <div className={classes.row}>
              <span className={classes.cpScoreHeaderChild} />
              <Link className={classes.link} onClick={onOpenTasks} to="#">
                {renderTasksCount()}
              </Link>
            </div>
          )}

          {task && <TasksComments tasks={tasks} />}

          {!!size(comments) && comments.map((com) => <CommentContainer comment={com} />)}
          {showComment && <CommentContainer checkpoint={checkpoint} onClose={() => setShowComment(false)} onSave={props.onCommentSave} />}

          <DetailModal {...taskModal} />
          <CreateModal {...taskCreate} />
          <ObservationCreateModal {...props} {...subModal} showAdd={false} />
        </>
      )}
    </div>
  );
};

const CheckpointScorePrint = (props) => {
  const { key, checkpoint, assessmentTasks } = props;
  const [tasks, setTasks] = useState();
  const [showComment, setShowComment] = useState(false);

  const isSubScore = checkpoint.isSubScore;
  const hasCriteria = get(checkpoint, 'criteria');
  const hasNotes = get(checkpoint, 'notes');
  const hasImages = get(checkpoint, 'images');
  const cpOpen = hasNotes || hasImages ? true : false;
  const classes = useStyles(props);

  const isGobs = get(checkpoint, 'depth') === 2;

  useEffect(() => {
    if (!assessmentTasks) {
      return;
    }
    const hasTasks = assessmentTasks.filter((at) => {
      const scardid = get(at, 'scorecardScoreId') === get(checkpoint, 'scorecardScoreId');
      if (at.checkpointId === null && isGobs) {
        return scardid;
      }
      if (isSubScore && get(at, 'subScore.id') == get(checkpoint, 'id')) {
        return true;
      }
      return scardid && at.checkpointId == get(checkpoint, 'checkpointId');
    });
    if (hasTasks) {
      setTasks(hasTasks);
    }
  }, [assessmentTasks]);

  const renderTasksCount = () => {
    const counts = {
      open: 0,
      pending: 0,
      closed: 0,
    };
    tasks.map((t) => {
      const { title } = getTaskStatus(t);
      set(counts, title, get(counts, title, 0) + 1);
    });
    const order = ['open', 'pending', 'closed'];
    return order
      .filter((o) => !!get(counts, o))
      .map((o) => `${get(counts, o)} ${uppercaseFirst(o)} Task${get(counts, o) > 1 ? 's' : ''}`)
      .join(', ');
  };

  const task = get(tasks, '0');

  const isCategory = get(props, 'filter.listGroup') === 'category';
  const isSubCat = isSubScore && isCategory;
  const showContent = get(props, 'showContent', true);
  const showDisplayScore = get(props, 'showDisplayScore', true);

  const name = (() => {
    if (isGobs) {
      return 'General Observations';
    } else {
      if (isSubScore) {
        return isSubCat ? get(checkpoint, 'realm.name', checkpoint.name) : `${get(checkpoint, 'checklist.name')}: ${get(checkpoint, 'name')}`;
      }
      return checkpoint.name;
    }
  })();

  const score = (() => {
    if (isGobs) {
      return 'N/A';
    } else {
      return checkpoint.value;
    }
  })();

  const comments = get(
    get(props, 'assessment.data.scorecardScores', []).find((c) => c.id == get(checkpoint, 'scorecardScoreId')),
    'comments',
    []
  );

  return isGobs && !hasCriteria ? null : (
    <div className={classes.topic} key={key}>
      <div className={classes.rowTop}>
        <div className={classes.row}>
          {showDisplayScore && (
            <Typography variant="body2" className={classes.cpScoreHeaderChild}>
              {isSubScore || isGobs ? '\xa0' : checkpoint.displayScore}
            </Typography>
          )}
          <ScoreIcon selected={false} center score={score} disabled={isSubScore && isCategory} />
        </div>
        <Typography variant="body2">
          <strong>{name}</strong>
          &nbsp;
          {hasCriteria}
        </Typography>
      </div>
      {showContent && (
        <>
          {cpOpen && (
            <div className={classes.cpscoreHidden}>
              {!!hasNotes && <div className={classes.criteria}>{`Notes: ${hasNotes || ''}`}</div>}
              {!!hasImages && (
                <div className={clsx(classes.images, classes.imagesprint)}>
                  <ImageList style={classes.image} imageSize={undefined} spaceBetween={3} showCaption srcOverride={{ full: 'large', thumb: 'thumb' }} images={sortBy(hasImages, 'orderColumn')} />
                </div>
              )}
            </div>
          )}
          {task && (
            <div className={classes.row}>
              <span className={classes.cpScoreHeaderChild} />
              <Link className={classes.link}>{renderTasksCount()}</Link>
            </div>
          )}
        </>
      )}

      {task && <TasksComments tasks={tasks} />}

      {!!size(comments) && comments.map((com) => <CommentContainer comment={com} />)}
      {showComment && <CommentContainer checkpoint={checkpoint} />}
    </div>
  );
};

const urlParams = {
  include: 'scorecardOptions',
};

export const EditScore = (props) => {
  return <AEditScore {...props} />;
};

const AEditScore = (props) => {
  const classes = useStyles();
  const { checkpoint, checklist, assessment, addToQueue, scorecard } = props;
  const checkpointId = get(checkpoint, 'checkpointId');
  const isObs = !checkpointId;
  const [state, setState] = useState({
    isSending: false,
  })

  const [params] = useState({ useEffect: true, waitFor: isObs ? null : undefined, params: urlParams, build: ['checkpoints'], transformData: (d) => get(d, '0') });

  const { isView1, isView2, isView3, hasSubScores, showObservationGrid } = getDisplayConfigFrom(scorecard, props);
  
  const checkpointdata = useRetrieveData({ url: `/checkpoints/${checkpointId}` }, params);
  
  useEffect(() => {
    if (isObs) {
      checkpointdata.manuallySetData(checkpoint);
    }
  }, []);

  const { isSubScore } = checkpoint;

  const onFormReady = async (obj) => {
    try {
      if (obj.submits > 1) {
        //dont submit on first ready
        setState(s => ({ ...s, isSending: true }));
        const result = await obj.submitSubScore();
        props.updateScores(result);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setState(s => ({ ...s, isSending: false }));
    }
  };

  let SUBS = null;
  if (size(checkpoint.subscores)) {
    if (showObservationGrid) {
      SUBS = null;
    } else {
      SUBS = (
        <div style={isView1 || isView3 ? { marginLeft: 10, borderLeft: `4px solid ${colors.lightGreyBorder}` } : {}}>
          {checkpoint.subscores.map((subcheck) => (
            <AEditScore {...props} key={subcheck.id} checkpoint={subcheck} />
          ))}
        </div>
      );
    }
  }

  const SCORE = isSubScore ? <SubscoreForm {...props} canChangeTag={false} onFormReady={onFormReady} /> : isView2 && !isObs ? null : <CheckpointForm
    isCourse={props.isCourse}
    addToQueue={addToQueue}
    queue={props.queue}
    checkpoint={checkpoint}
    assessmentId={get(assessment, 'data.id')}
    checklist={checklist}
    checkpointdata={checkpointdata}
    loading={checkpointdata.loading}
    scorecard={scorecard}
    assessment={assessment}
    canEditInfo={isObs ? true : hasSubScores ? (isView1 ? true : false) : undefined}
    renderObservations={showObservationGrid ? () => {
      return (
        <div className={classes.roomBtnContainer}>
          {size(checkpoint.subscores) ? <ObservationCollapse {...props} canEdit checkpoint={checkpoint} /> : undefined}
          {!props.isTemplate && <ObservationCreateFab {...props} roomDropProps={{ placeholder: 'ADD INSTANCE' }} style={{ marginLeft: 16, marginBottom: 8 }} checkpoint={checkpoint} activeEdit={true} hasSubScores={true} />}
        </div>
      )
    } : undefined}
  />

  const blocked = state.isSending || (props.queue || []).find((q) => q.id === checkpoint.checkpointId);

  const renderLoader = () => {
    return <div className={classes.loadContainer}>{blocked && <LinearProgress />}</div>;
  };

  return checkpoint.error ? (
    <div>{`Error: ${get(checkpoint, 'error.message')}`}</div>
  ) : (
    <div className={classes.topicEdit}>
      {renderLoader()}
      {SCORE}
      {SUBS}
    </div>
  );
};
